import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, ErrorState, Spinner } from "@/components/ui";
import { formatDateTime } from "@/lib/format";
import { downloadUrl, previewType, previewUrl } from "./attachments";
import type { MailMessageDetail } from "./queries";
import {
  recipientsLabel,
  senderLabel,
  useMailMessage,
  useRetryMail,
  useSetMailRead,
} from "./queries";

/**
 * The body, wrapped as a document for a sandboxed iframe (.docs/09 §8.4).
 *
 * The server already sanitised the HTML with `nh3` — no scripts, no handlers,
 * no `javascript:`. This is the second lock, and it is the one that stops a
 * *tracking pixel*: the CSP allows images only from `data:` until the seller
 * asks for the rest, so opening an email does not tell the sender it was
 * opened, or from which IP.
 *
 * An image the email **carries** is not a pixel and is shown at once: it was
 * delivered with the message, so nobody learns anything from it being looked
 * at. `carried` holds those, already fetched and turned into `data:` URLs by
 * `useCarriedImages` below, which is what the one allowed scheme is for.
 *
 * `sandbox` without `allow-scripts` and without `allow-same-origin` means the
 * document cannot run anything and cannot reach the CRM's origin if it did.
 * `allow-popups` is what still lets a link open — in a new tab, because the
 * sanitiser writes `rel="noopener noreferrer"` and `<base target="_blank">`
 * is set below.
 */
export function bodyDocument(
  html: string,
  showImages: boolean,
  carried: ReadonlyMap<string, string> = NONE,
): string {
  const images = showImages ? "https: data:" : "data:";
  const body =
    carried.size === 0
      ? html
      : html.replace(new RegExp(CARRIED_IMAGE, "g"), (url) => carried.get(url) ?? url);
  return [
    "<!doctype html><html lang='es'><head><meta charset='utf-8'>",
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${images}; style-src 'unsafe-inline'; font-src data:; form-action 'none'">`,
    "<base target='_blank'>",
    "<style>body{margin:0;padding:12px;font:14px/1.5 system-ui,sans-serif;color:#201e1d;background:#f3f2f2;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style>",
    "</head><body>",
    body,
    "</body></html>",
  ].join("");
}

/**
 * Where the server points an image the email *carries* (.docs/09 §8.4).
 *
 * A body refers to its own images by `cid:`, which no browser resolves, and
 * Zoho hands the CRM its webmail URL instead, which wants a Zoho session
 * cookie. `reading.ensure_body` rewrites both into this path — the CRM's own
 * copy of the file — and this is what finds them again.
 */
const CARRIED_IMAGE = String.raw`/api/mail/attachments/\d+/preview/`;

const NONE: ReadonlyMap<string, string> = new Map();

function carriedImageUrls(html: string): string[] {
  return [...new Set(html.match(new RegExp(CARRIED_IMAGE, "g")) ?? [])];
}

/** One carried image as a `data:` URL, or `null` when it cannot be had. */
async function asDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const bytes = new Uint8Array(await response.arrayBuffer());
    // Built by hand rather than with a `FileReader`, which would hand back
    // the type the *blob* claims. This one is the server's answer, and the
    // server is the only party that applies D40's allowlist.
    const type = response.headers.get("Content-Type") || "application/octet-stream";
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return `data:${type};base64,${btoa(binary)}`;
  } catch {
    return null;
  }
}

/**
 * The images the body carries, fetched by the page and not by the iframe.
 *
 * Measured in Chromium on 2026-10-23: a `sandbox`ed iframe without
 * `allow-same-origin` has an opaque origin, so its own requests count as
 * cross-site and carry no `SameSite=Lax` cookie — not even back to the CRM,
 * which answers them with a redirect to the login screen. The page has the
 * session, so it fetches them and hands them over as `data:` URLs, which the
 * CSP above already allows and which cost the iframe no origin of its own.
 *
 * `null` while they are on their way: the body is held back rather than
 * painted once with broken images and then again with them.
 */
function useCarriedImages(html: string): ReadonlyMap<string, string> | null {
  const urls = useMemo(() => carriedImageUrls(html), [html]);
  const [fetched, setFetched] = useState<{
    html: string;
    images: ReadonlyMap<string, string>;
  } | null>(null);

  useEffect(() => {
    if (urls.length === 0) return;
    let live = true;
    void Promise.all(
      urls.map(async (url) => [url, await asDataUrl(url)] as const),
    ).then((pairs) => {
      // One that failed stays as it was: the CSP refuses it and the seller
      // sees a broken image, which is the truth about it.
      if (live) {
        setFetched({
          html,
          images: new Map(
            pairs.filter((pair): pair is [string, string] => pair[1] !== null),
          ),
        });
      }
    });
    return () => {
      live = false;
    };
  }, [html, urls]);

  if (fetched?.html === html) return fetched.images;
  return urls.length === 0 ? NONE : null;
}

/** True when the body asks for something the CSP is currently refusing. */
function hasRemoteImages(html: string): boolean {
  return /<img[^>]+src=["']?https?:/i.test(html);
}

function Header({ message }: { message: MailMessageDetail }) {
  const { t } = useTranslation();
  return (
    <div className="border-b-2 border-rule px-4 py-3">
      <h2 className="text-[19px] font-extrabold">
        {message.subject || t("mail.noSubject")}
      </h2>
      <dl className="mt-2 grid gap-x-3 gap-y-1 text-xs text-mute-600 sm:grid-cols-[auto_1fr]">
        <dt className="sm:text-right">{t("mail.from")}</dt>
        <dd className="break-words text-ink">
          {senderLabel(message)}{" "}
          <span className="text-mute-600">&lt;{message.from_address}&gt;</span>
        </dd>
        <dt className="sm:text-right">{t("mail.to")}</dt>
        <dd className="break-words text-ink">{recipientsLabel(message.to)}</dd>
        {message.cc.length > 0 ? (
          <>
            <dt className="sm:text-right">{t("mail.cc")}</dt>
            <dd className="break-words text-ink">{recipientsLabel(message.cc)}</dd>
          </>
        ) : null}
        <dt className="sm:text-right">{t("mail.date")}</dt>
        <dd className="text-ink">{formatDateTime(message.occurred_at)}</dd>
      </dl>
    </div>
  );
}

export function ReadingPane({
  messageId,
  onReply,
  onForward,
}: {
  messageId: number;
  onReply: (message: MailMessageDetail, all: boolean) => void;
  onForward: (message: MailMessageDetail) => void;
}) {
  const { t } = useTranslation();
  const query = useMailMessage(messageId);
  const setRead = useSetMailRead();
  const retry = useRetryMail();
  const [showImages, setShowImages] = useState(false);
  const message = query.data;

  // Opening an email is reading it: the badge clears here and Zoho is told by
  // the server (.docs/09 §4.3). Only for what arrived — a sent message was
  // never unread.
  const isUnreadInbox = message?.folder === "inbox" && message.is_read === false;
  useEffect(() => {
    if (isUnreadInbox) setRead.mutate({ id: messageId, read: true });
    // Deliberately not depending on `setRead`: the mutation object is a new
    // reference after every settled request, which would loop.
  }, [messageId, isUnreadInbox]);

  // A different message is a different decision about its images.
  useEffect(() => setShowImages(false), [messageId]);

  const carried = useCarriedImages(message?.body_html ?? "");
  const page = useMemo(
    () => bodyDocument(message?.body_html ?? "", showImages, carried ?? NONE),
    [message?.body_html, showImages, carried],
  );

  if (query.isPending) return <Spinner label={t("mail.loadingMessage")} />;
  if (query.isError || !message) {
    return (
      <ErrorState
        message={
          query.error instanceof Error ? query.error.message : t("mail.loadFailed")
        }
        onRetry={() => void query.refetch()}
      />
    );
  }

  const blocked = !showImages && hasRemoteImages(message.body_html);
  // The images the body shows are not in this list: they are in the body, and
  // a chip for every signature logo buries the one file the seller wants.
  const files = message.attachments.filter((attachment) => !attachment.is_inline);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header message={message} />

      <div className="flex flex-wrap items-center gap-2 border-b border-rule px-4 py-2">
        <Button variant="primary" onClick={() => onReply(message, false)}>
          {t("mail.reply")}
        </Button>
        {/* Always offered, not only when there is a second recipient: a button
            that comes and goes is one a seller has to look for. */}
        <Button onClick={() => onReply(message, true)}>{t("mail.replyAll")}</Button>
        <Button onClick={() => onForward(message)}>{t("mail.forward")}</Button>
        {message.folder === "inbox" ? (
          <Button onClick={() => setRead.mutate({ id: message.id, read: false })}>
            {t("mail.markUnread")}
          </Button>
        ) : null}
        {message.status === "failed" ? (
          <Button
            variant="primary"
            disabled={retry.isPending}
            onClick={() => retry.mutate(message.id)}
          >
            {t("mail.retry")}
          </Button>
        ) : null}
      </div>

      {message.status === "failed" && message.error_message ? (
        <p
          role="alert"
          className="border-b border-rule px-4 py-2 text-xs text-brand-700"
        >
          {message.error_message}
        </p>
      ) : null}

      {files.length > 0 ? (
        <ul className="flex flex-wrap gap-2 border-b border-rule px-4 py-2 text-xs">
          {files.map((attachment) => {
            // Plain anchors: the file is streamed from Zoho by a
            // permission-checked view, and the browser shows it or saves it.
            const shows = previewType(attachment.name) !== null;
            return (
              <li key={attachment.id} className="flex items-center">
                <a
                  className="btn btn-secondary px-2 py-1"
                  {...(shows
                    ? {
                        href: previewUrl(attachment.id),
                        target: "_blank",
                        rel: "noopener noreferrer",
                        title: t("mail.attachment.preview"),
                      }
                    : {
                        href: downloadUrl(attachment.id),
                        download: attachment.name,
                        title: t("mail.attachment.noPreview"),
                      })}
                >
                  📎 {attachment.name}
                </a>
                {shows ? (
                  <a
                    className="btn btn-ghost px-2 py-1"
                    href={downloadUrl(attachment.id)}
                    download={attachment.name}
                    aria-label={t("mail.attachment.download", {
                      name: attachment.name,
                    })}
                  >
                    ⤓
                  </a>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {blocked ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-rule bg-panel px-4 py-2 text-xs">
          <span className="text-mute-600">{t("mail.imagesBlocked")}</span>
          <Button onClick={() => setShowImages(true)}>{t("mail.showImages")}</Button>
        </div>
      ) : null}

      {carried === null ? (
        <div className="min-h-0 flex-1">
          <Spinner label={t("mail.loadingImages")} />
        </div>
      ) : (
        <iframe
          title={message.subject || t("mail.noSubject")}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={page}
          className="min-h-0 w-full flex-1 border-0 bg-ground"
        />
      )}
    </div>
  );
}
